import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000",
  headers: {
    "Content-Type": "application/json",
  },
})

// Same pattern as BadgeRequestService: one shared instance + one interceptor,
// so every call automatically carries both headers. This is what the old
// per-method getAuthHeaders() should have been — that shape is how
// getExhibitionHeader() ended up imported but never actually used, which
// caused bulk-generate to silently drop X-Exhibition-ID and fall back to
// the wrong exhibition (EXHIBITION_MISMATCH).
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
      Object.assign(config.headers, getExhibitionHeader())
    }
    return config
  },
  (error) => Promise.reject(error),
)

export interface BulkBadgeRequest {
  print_order_id: number
  staff_representations: number[]
}

export interface BulkBadgeResult {
  representation_id: number
  badge_data: {
    badge_info: {
      person_full_name: string
      role: string
      exhibitor_name: string
      exhibition_title: string
      stand_category: string
      verification_code: string
      role_color?: string
    }
    photo_url: string | null
    has_photo: boolean
  }
  template_config: any
  verification_code: string
  success: boolean
}

export interface BulkBadgeResponse {
  success: boolean
  message: string
  badge_results: BulkBadgeResult[]
  failed_badges: Array<{
    representation_id: number
    error: string
    success: false
  }>
  total_processed: number
  successful_count: number
  failed_count: number
}

class BulkBadgeService {
  async generateBulkBadges(request: BulkBadgeRequest): Promise<BulkBadgeResponse> {
    try {
      console.log("BulkBadgeService: Generating bulk badges with request:", request)

      const response = await api.post("/api/badges/bulk-generate/", request, {
        timeout: 60000, // 60 second timeout for bulk operations
      })

      console.log("BulkBadgeService: Bulk generation response:", response.data)
      return response.data
    } catch (error: any) {
      console.error("BulkBadgeService: Error generating bulk badges:", error)

      if (error.response?.data) {
        throw error.response.data
      }

      throw {
        success: false,
        message: error.message || "Failed to generate bulk badges",
        badge_results: [],
        failed_badges: [],
        total_processed: 0,
        successful_count: 0,
        failed_count: 0,
      }
    }
  }

  async validatePrintOrder(orderId: number): Promise<{ valid: boolean; message: string }> {
    try {
      const response = await api.get(`/api/badges/print-orders/${orderId}/validation/`)

      return response.data
    } catch (error: any) {
      console.error("BulkBadgeService: Error validating print order:", error)
      return {
        valid: false,
        message: error.response?.data?.message || "Failed to validate print order",
      }
    }
  }

  async getBulkGenerationStatus(orderId: number): Promise<any> {
    try {
      const response = await api.get(`/api/badges/print-orders/${orderId}/bulk-status/`)

      return response.data
    } catch (error: any) {
      console.error("BulkBadgeService: Error getting bulk status:", error)
      throw error
    }
  }
}

export const bulkBadgeService = new BulkBadgeService()